import React from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router';
import {
  postMessage,
  ThreadGoneError,
} from '../../api/agentChat/threads/client';
import { createTask as createTaskApi } from '../../api/tasks/client';
import type { TaskPriority } from '../../api/tasks/interface';
import { removeArtifactAction } from '../../data/artifacts/logic';
import { useSelector } from '../../data/store';
import useTasksHook from '../../data/tasks/useTasksHook';
import useFilesHook from '../ClaimOverview/FilesListContainer/useFilesHook';
import { useEscDossierContext } from '../DossierContext';
import { formatDue } from '../TasksTab/grouping';
import { useChatMessages } from './hooks/useChatMessages';
import { useChatPrefill } from './hooks/useChatPrefill';
import { useChatStreamSubscriptions } from './hooks/useChatStreamSubscriptions';
import { useFileUpload } from './hooks/useFileUpload';
import { useHighlightScroll } from './hooks/useHighlightScroll';
import { useNoteMode } from './hooks/useNoteMode';
import { useProposalActions } from './hooks/useProposalActions';
import { useUrlMentionInsertion } from './hooks/useUrlMentionInsertion';
import ChatComposer from './parts/ChatComposer';
import MessageList from './parts/MessageList';
import NoteComposer from './parts/NoteComposer';
import PendingProposalsPanel from './parts/PendingProposalsPanel';
import UploadConfirmCard from './parts/UploadConfirmCard';
import styles from './Chat.module.scss';
import type { MentionInputHandle } from './MentionInput';

interface EscChatProps {
  dossierId: string;
  threadId: string;
  pendingPrefill?: {
    token: number;
    mode: 'message' | 'note';
    text: string;
  } | null;
  onPrefillConsumed?: () => void;
  onThreadGone?: (threadId: string) => void;
}

interface ParsedTaskCommand {
  title: string;
  due: string | null;
  priority: TaskPriority;
}

// `/task <title> [due:YYYY-MM-DD] [!high|!med|!low]` — returns null when the
// text isn't a task command so the caller falls through to the normal LLM send.
function parseTaskCommand(text: string): ParsedTaskCommand | null {
  if (!/^\/task\b/i.test(text)) {
    return null;
  }
  let body = text.replace(/^\/task\b/i, '').trim();
  let due: string | null = null;
  let priority: TaskPriority = 'med';

  const dueMatch = body.match(/\bdue:(\d{4}-\d{2}-\d{2})\b/);
  if (dueMatch) {
    due = dueMatch[1];
    body = body.replace(dueMatch[0], '').trim();
  }
  const prioMatch = body.match(/(?:^|\s)!(high|med|low)\b/i);
  if (prioMatch) {
    priority = prioMatch[1].toLowerCase() as TaskPriority;
    body = body.replace(prioMatch[0], '').trim();
  }
  return { title: body || 'Untitled task', due, priority };
}

const EscChat: React.FC<EscChatProps> = ({
  dossierId,
  threadId,
  pendingPrefill,
  onPrefillConsumed,
  onThreadGone,
}) => {
  const handleThreadGone = React.useCallback(
    (err: unknown) => {
      if (err instanceof ThreadGoneError) {
        onThreadGone?.(threadId);
        return true;
      }
      return false;
    },
    [onThreadGone, threadId]
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightMessageId = searchParams.get('highlight');
  const insertMention = searchParams.get('insert_mention');
  const insertMentionLabel = searchParams.get('insert_mention_label');
  const { onFileClick, onTaskClick, onArtifactClick, onArtifactReplace } =
    useEscDossierContext();

  const { displayMessages, setDisplayMessages, displayMessagesRef, loading } =
    useChatMessages(dossierId, threadId, handleThreadGone);
  useChatStreamSubscriptions(
    dossierId,
    threadId,
    setDisplayMessages,
    displayMessagesRef,
    onArtifactClick,
    onArtifactReplace
  );

  const currentUser = useSelector((s) => s.session.current);
  const [{ tasks, hasData: hasTasksData }, tasksHandler] =
    useTasksHook(dossierId);
  const tasksFetchedRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!dossierId || hasTasksData || tasksFetchedRef.current === dossierId) {
      return;
    }
    tasksFetchedRef.current = dossierId;
    tasksHandler.fetchTasks();
  }, [dossierId, hasTasksData, tasksHandler]);

  const [{ files: filesRecord }] = useFilesHook(dossierId);
  const files = React.useMemo(() => Object.values(filesRecord), [filesRecord]);
  const dispatch = useDispatch();
  const [inputEmpty, setInputEmpty] = React.useState(true);
  const [sending, setSending] = React.useState(false);
  const listRef = React.useRef<HTMLDivElement>(null);
  const mentionInputRef = React.useRef<MentionInputHandle>(null);

  const {
    noteMode,
    setNoteMode,
    noteText,
    setNoteText,
    noteSubmitting,
    enableNoteMode,
    handleNoteSubmit,
    cancelNote,
  } = useNoteMode(dossierId);

  const {
    pendingFileForUpload,
    setPendingFileForUpload,
    fileTitle,
    setFileTitle,
    fileUploading,
    dragging,
    fileInputRef,
    triggerFileSelect,
    handleConfirmUpload,
    cancelUpload,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
  } = useFileUpload(dossierId, setDisplayMessages);

  useChatPrefill(
    pendingPrefill,
    onPrefillConsumed,
    setNoteMode,
    setNoteText,
    mentionInputRef
  );

  const { highlightedId } = useHighlightScroll(
    displayMessages,
    listRef,
    highlightMessageId
  );

  useUrlMentionInsertion(
    insertMention,
    insertMentionLabel,
    tasks,
    mentionInputRef,
    setSearchParams
  );

  const { pendingPanel, proposalSubmitting, handleApprove, handleReject } =
    useProposalActions(
      dossierId,
      threadId,
      displayMessages,
      setDisplayMessages,
      handleThreadGone
    );

  const inputLocked = pendingPanel !== null;

  const handleCommand = React.useCallback(
    (cmd: 'upload' | 'note') => {
      if (cmd === 'upload') {
        triggerFileSelect();
      }
      if (cmd === 'note') {
        enableNoteMode();
      }
    },
    [triggerFileSelect, enableNoteMode]
  );

  const handleSend = React.useCallback(() => {
    if (!dossierId || sending || inputLocked) {
      return;
    }
    const text = mentionInputRef.current?.getValue().trim() ?? '';
    if (!text) {
      return;
    }
    mentionInputRef.current?.clear();
    setInputEmpty(true);

    // `/task …` is handled locally — create the task via the API and only
    // acknowledge success after it persists (so a failed create doesn't claim
    // a task was made).
    const taskCommand = parseTaskCommand(text);
    if (taskCommand) {
      setDisplayMessages((prev) => [
        ...prev,
        {
          message_id: `task-cmd-${Date.now()}`,
          role: 'USER',
          text,
          created_at: new Date().toISOString(),
        },
      ]);
      void (async () => {
        try {
          const created = await createTaskApi({
            dossierId,
            title: taskCommand.title,
            due: taskCommand.due,
            priority: taskCommand.priority,
            assignee:
              currentUser?.full_name?.trim() || currentUser?.email || 'You',
            source: 'chat',
          });
          // Insert the created task through Redux (respects the in-flight-fetch
          // guard) so it shows in the panel + @-mention list without a refetch.
          tasksHandler.addTask(created);
          const dueSuffix = taskCommand.due
            ? ` · due ${formatDue(taskCommand.due)}`
            : '';
          setDisplayMessages((prev) => [
            ...prev,
            {
              message_id: `task-ack-${Date.now()}`,
              role: 'ASSISTANT',
              text: `Task created: ${taskCommand.title}${dueSuffix} — find it in the Tasks panel.`,
              created_at: new Date().toISOString(),
            },
          ]);
        } catch {
          setDisplayMessages((prev) => [
            ...prev,
            {
              message_id: `task-err-${Date.now()}`,
              role: 'ASSISTANT',
              text: "Couldn't create the task — please try again.",
              created_at: new Date().toISOString(),
            },
          ]);
        }
      })();
      return;
    }

    const tempId = `optimistic-${Date.now()}`;
    setDisplayMessages((prev) => [
      ...prev,
      {
        message_id: tempId,
        role: 'USER',
        text,
        created_at: new Date().toISOString(),
      },
    ]);

    setSending(true);
    // The post response carries only IDs (`{thread_id, user_message_id,
    // agent_message_id}`); the message contents arrive over the streaming
    // bus (`onStarted` → `onChunk` → `onComplete`). No reconciliation needed
    // on success — leave the optimistic temp message in place; `fetchMessages`
    // on next mount replaces it with the canonical row.
    void postMessage(dossierId, threadId, text)
      .catch((err) => {
        handleThreadGone(err);
        setDisplayMessages((prev) =>
          prev.filter(
            (m) => m.message_id !== tempId && m.message_id !== threadId
          )
        );
        dispatch(removeArtifactAction(dossierId, threadId));
      })
      .finally(() => setSending(false));
  }, [
    dossierId,
    threadId,
    sending,
    inputLocked,
    handleThreadGone,
    setDisplayMessages,
    tasksHandler,
    currentUser,
  ]);

  const handleInputChange = React.useCallback((serialized: string) => {
    setInputEmpty(!serialized.trim());
  }, []);

  const handleCitationClick = React.useCallback(
    (fileId: string, page?: number) => {
      onFileClick(fileId, page);
    },
    [onFileClick]
  );

  const handleMentionClick = React.useCallback(
    (type: 'file' | 'task', id: string) => {
      if (type === 'task') {
        onTaskClick(id);
      } else {
        onFileClick(id);
      }
    },
    [onFileClick, onTaskClick]
  );

  return (
    <div className={styles.splitContainer}>
      <div
        className={styles.chatPane}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {dragging && <div className={styles.draggingOverlay} />}
        <input
          ref={fileInputRef}
          type="file"
          style={{ display: 'none' }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) {
              setPendingFileForUpload(f);
              setFileTitle(f.name.replace(/\.[^.]+$/, ''));
            }
            if (fileInputRef.current) {
              fileInputRef.current.value = '';
            }
          }}
        />
        <MessageList
          displayMessages={displayMessages}
          dossierId={dossierId}
          threadId={threadId}
          loading={loading}
          highlightedId={highlightedId}
          files={files}
          tasks={tasks}
          proposalSubmitting={proposalSubmitting}
          handleApprove={handleApprove}
          handleReject={handleReject}
          handleCitationClick={handleCitationClick}
          handleMentionClick={handleMentionClick}
          listRef={listRef}
        />

        <div className={styles.inputBar}>
          {pendingPanel ? (
            <PendingProposalsPanel
              messageId={pendingPanel.messageId}
              proposals={pendingPanel.proposals}
              proposalSubmitting={proposalSubmitting}
              onApprove={handleApprove}
              onReject={handleReject}
            />
          ) : pendingFileForUpload ? (
            <UploadConfirmCard
              file={pendingFileForUpload}
              fileTitle={fileTitle}
              setFileTitle={setFileTitle}
              fileUploading={fileUploading}
              onConfirm={handleConfirmUpload}
              onCancel={cancelUpload}
            />
          ) : noteMode ? (
            <NoteComposer
              noteText={noteText}
              setNoteText={setNoteText}
              noteSubmitting={noteSubmitting}
              onSubmit={handleNoteSubmit}
              onCancel={cancelNote}
            />
          ) : (
            <ChatComposer
              mentionInputRef={mentionInputRef}
              dossierId={dossierId}
              sending={sending}
              inputEmpty={inputEmpty}
              tasks={tasks}
              onInputChange={handleInputChange}
              onSend={handleSend}
              onMentionClick={handleMentionClick}
              onCommand={handleCommand}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default EscChat;
